/**
 * Per-model display-name overrides.
 *
 * Friendly singular/plural labels surfaced in tabs, breadcrumbs, headers,
 * and list views. Resolution order at runtime:
 *
 *   1. `displayOverrides[model].label` / `labelPlural` (this file)
 *   2. `modelDisplayConfig[model].label` / `labelPlural` (codegen — the
 *      raw Neo4j label, e.g. "RFLPRequirement")
 *   3. CamelCase split of the model class name as last-ditch fallback.
 *
 * Unlisted models fall through to the codegen default. Only override
 * what needs renaming; partial entries (just `label` or just
 * `labelPlural`) are fine — the missing one falls through.
 *
 * No codegen run or backend restart required after edits — pure FE config.
 */

export const displayOverrides: Record<
  string,
  { label?: string; labelPlural?: string }
> = {
  // RFLP — strip the module prefix; tabs read "Requirements" not
  // "RFLPRequirements".
  Project: { label: "Project", labelPlural: "Projects" },
  Requirement: { label: "Requirement", labelPlural: "Requirements" },
  Action: { label: "Action", labelPlural: "Actions" },
  ActionInputParameter: {
    label: "Input Parameter",
    labelPlural: "Input Parameters",
  },
  ActionOutputParameter: {
    label: "Output Parameter",
    labelPlural: "Output Parameters",
  },
  ItemFlow: { label: "Item Flow", labelPlural: "Item Flows" },
  LogicalPart: { label: "Logical Part", labelPlural: "Logical Parts" },
  PhysicalPart: { label: "Physical Part", labelPlural: "Physical Parts" },
  Port: { label: "Port", labelPlural: "Ports" },
  PhysicsPort: { label: "Physics Port", labelPlural: "Physics Ports" },
  ControlPort: { label: "Control Port", labelPlural: "Control Ports" },
  InterfacePort: { label: "Interface Port", labelPlural: "Interface Ports" },
  Connection: { label: "Connection", labelPlural: "Connections" },
  Constraint: { label: "Constraint", labelPlural: "Constraints" },
  PropertyType: { label: "Property Type", labelPlural: "Property Types" },
  PropertyValue: { label: "Property Value", labelPlural: "Property Values" },
  Unit: { label: "Unit", labelPlural: "Units" },
  Standard: { label: "Standard", labelPlural: "Standards" },
  ChangeImpact: { label: "Change Impact", labelPlural: "Change Impacts" },
  ChangeProposal: { label: "Change Proposal", labelPlural: "Change Proposals" },
  ProposalBatch: { label: "Proposal Batch", labelPlural: "Proposal Batches" },
  ChangePropagation: {
    label: "Change Propagation",
    labelPlural: "Change Propagations",
  },
  AnalysisRecord: {
    label: "Analysis Record",
    labelPlural: "Analysis Records",
  },
  Completeness: { label: "Completeness", labelPlural: "Completeness" },
  Succession: { label: "Succession", labelPlural: "Successions" },
};
